import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, useToast } from '@kss/ui';
import {
  apiClient,
  restaurantOrderService,
  restaurantTableService,
  ORDER_STATUSES,
  CUSTOMER_VISIBLE_STEPS,
  KITCHEN_OPEN_STATES,
  nextStatus,
  statusColor,
  type RestaurantOrder,
  type RestaurantOrderStatus,
  type RestaurantTable,
} from '@kss/api';
import { ChefHat, RefreshCw, Filter, Volume2, VolumeX, Timer, MapPin } from 'lucide-react';

/**
 * Kitchen Display System (KDS).
 *
 * Two views over the live kitchen queue:
 *  - Board (default): three lanes — To Prepare → Cooking → Ready · Pass —
 *    and a ticket moves across the lanes as the cook taps one button
 *    (Confirm → Accept → Start Cooking → Mark Ready → Hand to Waiter).
 *    The Ready lane is the "pass": plated tickets waiting for pickup.
 *  - Grid: the classic flat card grid with a status filter.
 *
 * Cards age into an urgency accent — amber after 10 min, red after 20 —
 * so the longest-waiting tickets stand out.
 *
 * Polls every 10s for fresh tickets. When a new kitchen-visible ticket
 * appears we play a chime. Toggle the mute button to silence it.
 *
 * Backend contract pending — until §29 lands we read/write the
 * `status` field on the existing `RestaurantOrder` CRUD endpoint
 * directly. That field is free-text on the live API today; we
 * just write the canonical enum values.
 */

interface KitchenOrder {
  id: string;
  code: string;
  status: RestaurantOrderStatus;
  remarks?: string;
  description?: string; // dine-in stuffs the line items JSON here
  tableId?: string;
  tableCode?: string;
  placedAt?: string;
  items?: Array<{ name: string; quantity: number; note?: string }>;
  customerName?: string;
}

const POLL_MS = 10_000;

/**
 * The KDS shows the kitchen's own pipeline PLUS the "Ready" pass lane so
 * cooks can see what's plated and waiting for a waiter to pick up.
 */
const KDS_VISIBLE: ReadonlySet<RestaurantOrderStatus> = new Set<RestaurantOrderStatus>([
  ...KITCHEN_OPEN_STATES,
  'Ready',
]);

/** Lane-board columns — tickets flow left → right as the kitchen advances. */
const KDS_LANES: Array<{ key: string; title: string; statuses: RestaurantOrderStatus[]; accent: string; hint: string }> = [
  { key: 'queue', title: 'To Prepare', statuses: ['WaiterConfirmed', 'Accepted'], accent: '#2563eb', hint: 'Confirmed tickets waiting to start' },
  { key: 'cooking', title: 'Cooking', statuses: ['Cooking'], accent: '#d97706', hint: 'On the stove right now' },
  { key: 'ready', title: 'Ready · Pass', statuses: ['Ready'], accent: '#16a34a', hint: 'Plated — waiting for pickup' },
];

/** Per-status one-tap advance label (covers the full kitchen pipeline). */
const ADVANCE_LABEL: Partial<Record<RestaurantOrderStatus, string>> = {
  WaiterConfirmed: 'Confirm',
  Accepted: 'Accept',
  Cooking: 'Start Cooking',
  Ready: 'Mark Ready',
  Delivered: 'Hand to Waiter',
  Served: 'Mark Served',
  Billed: 'Close',
  Paid: 'Mark Paid',
};

/** Minutes since the ticket was placed → urgency accent (amber 10m, red 20m). */
function urgencyAccent(placedAt?: string): string | null {
  if (!placedAt) return null;
  const mins = (Date.now() - new Date(placedAt).getTime()) / 60_000;
  if (mins >= 20) return '#dc2626';
  if (mins >= 10) return '#d97706';
  return null;
}

export default function KitchenDisplay() {
  const toast = useToast();
  const [orders, setOrders] = useState<KitchenOrder[]>([]);
  const [tables, setTables] = useState<Map<string, string>>(new Map()); // tableId → "T4"
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [filter, setFilter] = useState<RestaurantOrderStatus | 'all'>('all');
  const [view, setView] = useState<'board' | 'grid'>('board');
  const [lastFetchAt, setLastFetchAt] = useState<number>(Date.now());
  const [knownIds, setKnownIds] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [rows, tableRows] = await Promise.all([
        restaurantOrderService.all(),
        restaurantTableService.all().catch(() => [] as RestaurantTable[]),
      ]);
      const tableMap = new Map<string, string>();
      for (const t of tableRows) {
        if (t.restaurantTableId) tableMap.set(String(t.restaurantTableId), t.code ?? t.name ?? '');
      }
      setTables(tableMap);

      const parsed: KitchenOrder[] = rows.map((r) => parseOrder(r as RestaurantOrder));
      // Kitchen only sees orders after the waiter has confirmed them
      // (or, for legacy tickets created before the waiter step
      // existed, anything between Accepted and Ready). Pre-waiter
      // orders live on the Waiter Queue, not the KDS.
      const open = parsed
        .filter((o) => o.status && KDS_VISIBLE.has(o.status))
        .sort((a, b) => (a.placedAt ?? '').localeCompare(b.placedAt ?? ''));

      // Audible ping when a NEW kitchen-visible ticket appears.
      const fresh = open.filter((o) => !knownIds.has(o.id));
      if (fresh.length && !muted) playChime();

      setOrders(open);
      setKnownIds(new Set(open.map((o) => o.id)));
      setLastFetchAt(Date.now());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load kitchen queue.');
    } finally {
      setLoading(false);
    }
  }, [knownIds, muted]);

  useEffect(() => {
    void refresh();
    const t = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(t);
  }, [refresh]);

  async function advance(o: KitchenOrder, target?: RestaurantOrderStatus) {
    const desired = target ?? nextStatus(o.status);
    if (!desired) return;
    try {
      await restaurantOrderService.update([
        // We have to send the whole row back per the CRUD contract.
        // Persist the new status plus a small audit trail in remarks
        // until §29 lands proper timestamp columns.
        {
          id: Number(o.id),
          code: o.code,
          name: tables.get(o.tableId ?? '') ?? o.code,
          description: o.description,
          // Workflow stage → `orderStatus` (string). `status` stays the
          // numeric row enum (0) — sending the string here 400s.
          status: 0,
          orderStatus: desired,
          remarks: appendAudit(o.remarks, desired),
          isActive: 1,
        } as unknown as Parameters<typeof restaurantOrderService.update>[0][number],
      ]);
      // Optimistic update so the UI reacts before the next poll.
      setOrders((prev) =>
        desired === 'Delivered' || desired === 'Served' || desired === 'Billed' || desired === 'Cancelled' || desired === 'Voided'
          ? prev.filter((p) => p.id !== o.id)
          : prev.map((p) => (p.id === o.id ? { ...p, status: desired } : p)),
      );
      toast.success('Status updated', `${o.code ?? `#${o.id}`} → ${desired}`);
    } catch (e) {
      toast.error('Update failed', e instanceof Error ? e.message : 'Try again');
    }
  }

  const filtered = useMemo(
    () => (filter === 'all' ? orders : orders.filter((o) => o.status === filter)),
    [orders, filter],
  );

  return (
    <div className="-m-6 flex h-[calc(100vh-3.5rem)] flex-col">
      {/* Header bar */}
      <header className="flex items-center gap-3 border-b border-border bg-surface-card px-4 py-3">
        <ChefHat className="text-orange-500" size={22} />
        <div className="flex-1">
          <h1 className="text-base font-semibold text-content-primary">Kitchen Display</h1>
          <p className="text-[11px] text-content-muted">
            {orders.length} open tickets · last refreshed {fmtAgo(lastFetchAt)}
          </p>
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-border">
          <button
            onClick={() => setView('board')}
            className={`px-2.5 py-1 text-xs ${view === 'board' ? 'bg-primary text-white' : 'bg-surface-card text-content-secondary'}`}
          >
            Board
          </button>
          <button
            onClick={() => setView('grid')}
            className={`px-2.5 py-1 text-xs ${view === 'grid' ? 'bg-primary text-white' : 'bg-surface-card text-content-secondary'}`}
          >
            Grid
          </button>
        </div>
        {view === 'grid' && (
          <>
            <Filter size={14} className="text-content-muted" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as typeof filter)}
              className="h-8 rounded-md border border-border bg-surface-card px-2 text-xs text-content-primary"
            >
              <option value="all">All</option>
              {ORDER_STATUSES.filter((s) => KDS_VISIBLE.has(s)).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </>
        )}
        <Button variant="outline" size="sm" onClick={() => void refresh()}>
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={() => setMuted((v) => !v)} title={muted ? 'Unmute new-order chime' : 'Mute new-order chime'}>
          {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
        </Button>
      </header>

      {err && (
        <Alert variant="danger" className="m-3">
          {err}
        </Alert>
      )}

      {/* Board (lanes) / Grid */}
      <div className="flex-1 overflow-hidden p-3">
        {loading && orders.length === 0 ? (
          <div className="grid place-items-center py-20 text-sm text-content-muted">
            Loading kitchen queue…
          </div>
        ) : orders.length === 0 ? (
          <div className="grid place-items-center py-20 text-center text-sm text-content-muted">
            <ChefHat className="mb-2 text-content-muted" size={28} />
            No open tickets. New orders will appear here.
          </div>
        ) : view === 'board' ? (
          <div className="flex h-full gap-3 overflow-x-auto">
            {KDS_LANES.map((lane) => {
              const laneOrders = orders.filter((o) => lane.statuses.includes(o.status));
              return (
                <section
                  key={lane.key}
                  className="flex h-full min-w-[280px] flex-1 flex-col overflow-hidden rounded-lg border border-border bg-surface-app"
                  style={{ borderTopColor: lane.accent, borderTopWidth: 3 }}
                >
                  <header className="flex items-center justify-between border-b border-border px-3 py-2">
                    <div>
                      <div className="text-sm font-semibold text-content-primary">{lane.title}</div>
                      <div className="text-[10px] text-content-muted">{lane.hint}</div>
                    </div>
                    <span
                      className="grid h-6 min-w-[24px] place-items-center rounded-full px-1.5 text-xs font-semibold text-white"
                      style={{ background: lane.accent }}
                    >
                      {laneOrders.length}
                    </span>
                  </header>
                  <div className="flex-1 space-y-2 overflow-y-auto p-2">
                    {laneOrders.length === 0 ? (
                      <div className="grid place-items-center py-8 text-[11px] text-content-muted">— empty —</div>
                    ) : (
                      laneOrders.map((o) => (
                        <OrderCard
                          key={o.id}
                          order={o}
                          tableLabel={tables.get(o.tableId ?? '') ?? o.tableCode}
                          onAdvance={(t) => void advance(o, t)}
                        />
                      ))
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        ) : filtered.length === 0 ? (
          <div className="grid h-full place-items-center text-center text-sm text-content-muted">
            No tickets match this filter.
          </div>
        ) : (
          <div className="grid h-full content-start gap-3 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                tableLabel={tables.get(o.tableId ?? '') ?? o.tableCode}
                onAdvance={(t) => void advance(o, t)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderCard({
  order, tableLabel, onAdvance,
}: {
  order: KitchenOrder;
  tableLabel?: string;
  onAdvance: (target?: RestaurantOrderStatus) => void;
}) {
  const c = statusColor(order.status);
  const ago = order.placedAt ? fmtAgo(new Date(order.placedAt).getTime()) : '';
  const next = nextStatus(order.status);
  const urgent = urgencyAccent(order.placedAt);

  return (
    <Card
      padded={false}
      className="flex flex-col gap-2 p-3"
      style={urgent ? { borderLeftColor: urgent, borderLeftWidth: 3 } : undefined}
    >
      <div className="flex items-start gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-orange-50 text-orange-600">
          <MapPin size={16} />
        </div>
        <div className="flex-1">
          <div className="text-[15px] font-semibold leading-tight text-content-primary">
            {tableLabel ?? order.code ?? `#${order.id}`}
          </div>
          {order.customerName && (
            <div className="text-[11px] text-content-muted">{order.customerName}</div>
          )}
        </div>
        <span
          className="rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={{ background: c.bg, color: c.fg }}
        >
          {c.label}
        </span>
      </div>

      {ago && (
        <div className="flex items-center gap-1 text-[11px] text-content-muted">
          <Timer size={11} /> Placed {ago}
        </div>
      )}

      <ul className="flex flex-col gap-1 border-t border-border pt-2 text-[13px]">
        {(order.items ?? []).slice(0, 8).map((it, idx) => (
          <li key={idx} className="flex items-baseline gap-2">
            <span className="inline-flex h-5 min-w-[24px] items-center justify-center rounded bg-content-primary/5 px-1.5 text-[11px] font-semibold">
              {it.quantity}×
            </span>
            <div className="flex-1">
              <div className="text-content-primary">{it.name}</div>
              {it.note && <div className="text-[11px] italic text-content-muted">“{it.note}”</div>}
            </div>
          </li>
        ))}
        {(order.items?.length ?? 0) > 8 && (
          <li className="text-[11px] text-content-muted">
            …and {(order.items?.length ?? 0) - 8} more
          </li>
        )}
        {!order.items?.length && (
          <li className="text-[11px] italic text-content-muted">
            No line items captured — open the ticket in Orders to see notes.
          </li>
        )}
      </ul>

      {order.remarks && (
        <div className="rounded-md bg-orange-50 px-2 py-1.5 text-[11px] text-orange-800">
          “{order.remarks.split('|')[0]}”
        </div>
      )}

      <div className="mt-auto flex flex-wrap gap-1 border-t border-border pt-2">
        {next && (
          <Button size="sm" onClick={() => onAdvance(next)} className="flex-1">
            {ADVANCE_LABEL[next] ?? next}
          </Button>
        )}
        {order.status !== 'Placed' && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onAdvance('Cancelled')}
            title="Cancel this ticket"
          >
            Cancel
          </Button>
        )}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

interface DineDescriptionPayload {
  tableId?: string;
  tableCode?: string;
  customerName?: string;
  customerPhone?: string;
  orderNote?: string;
  items?: Array<{ name: string; quantity: number; note?: string }>;
  placedAt?: string;
}

function parseOrder(r: RestaurantOrder): KitchenOrder {
  // The dine-in flow stuffs the full payload JSON into `description`
  // when the dedicated endpoint isn't mounted yet (see §28 fallback).
  let parsed: DineDescriptionPayload = {};
  try {
    if (r.description && r.description.startsWith('{')) {
      parsed = JSON.parse(r.description) as DineDescriptionPayload;
    }
  } catch {
    /* leave parsed empty */
  }
  // Workflow stage lives in `orderStatus` (string); the numeric `status`
  // column always reads 0 post-§35, so prefer orderStatus.
  const rawStage = (r as { orderStatus?: string | null }).orderStatus ?? r.status ?? '';
  const validStatus = (ORDER_STATUSES as readonly string[]).includes(rawStage)
    ? (rawStage as RestaurantOrderStatus)
    : 'Placed';
  return {
    id: String(r.id ?? ''),
    code: r.code ?? `#${r.id ?? ''}`,
    status: validStatus,
    remarks: r.remarks ?? undefined,
    description: r.description ?? undefined,
    tableId: parsed.tableId,
    tableCode: parsed.tableCode,
    customerName: parsed.customerName,
    items: parsed.items,
    placedAt: parsed.placedAt ?? r.indate ?? undefined,
  };
}

function appendAudit(prev: string | undefined, newStatus: RestaurantOrderStatus): string {
  const stamp = new Date().toISOString().slice(11, 16);
  const entry = `${newStatus}@${stamp}`;
  if (!prev) return entry;
  // Keep last 4 audit entries, pipe-delimited.
  return [...prev.split('|').slice(-3), entry].join('|');
}

function fmtAgo(t: number): string {
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

let chimeCtx: AudioContext | null = null;
function playChime() {
  try {
    chimeCtx = chimeCtx ?? new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const osc = chimeCtx.createOscillator();
    const gain = chimeCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.value = 0.0001;
    osc.connect(gain).connect(chimeCtx.destination);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.18, chimeCtx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, chimeCtx.currentTime + 0.3);
    osc.stop(chimeCtx.currentTime + 0.32);
  } catch {
    /* audio context not available — ignore */
  }
}

// Re-export type for routes file
export type { KitchenOrder };

// Suppress unused warning — CUSTOMER_VISIBLE_STEPS is re-exported for sibling pages.
void CUSTOMER_VISIBLE_STEPS;
